import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { Audience, CampaignSpec } from '@vagusx/shared';
import type {
  CompileCampaignInput,
  CreateCampaignInput,
  PublishCampaignInput,
  UpdateCampaignInput,
  UpdateContactPolicyInput,
} from '@vagusx/clients';
import { getOutreachClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import { CACHE_TIME_10_MINS, CACHE_TIME_1_MIN } from '@/lib/query-client';

/**
 * Outreach authoring queries (ADR-154, phase 5).
 *
 * Two staleness policies, for two kinds of answer. The catalog and the campaign
 * list are configuration and use the shared 10-minute window. The audience
 * preview is a count of real patients that moves as the clinic works, so it gets
 * the 1-minute window — an author who tightens a filter and sees the same number
 * would conclude the filter did nothing.
 */

export function useAudienceCatalog() {
  return useQuery({
    queryKey: queryKeys.outreach.catalog(),
    queryFn: () => getOutreachClient().audienceCatalog(),
    staleTime: CACHE_TIME_10_MINS,
  });
}

export function useOutreachCampaigns() {
  return useQuery({
    queryKey: queryKeys.outreach.campaigns(),
    queryFn: () => getOutreachClient().list(),
    staleTime: CACHE_TIME_10_MINS,
  });
}

export function useOutreachCampaign(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.outreach.campaign(id ?? ''),
    queryFn: () => getOutreachClient().getById(id!),
    enabled: !!id,
  });
}

export function useCampaignVersions(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.outreach.versions(id ?? ''),
    queryFn: () => getOutreachClient().listVersions(id!),
    enabled: !!id,
  });
}

/**
 * How many patients match, from the same query the reconciler runs.
 *
 * **This is a population-scale query.** Callers that drive it from an input
 * must debounce (see `AudienceSection`), and callers that render one per row
 * should pass a long `staleTime` so revisiting the page does not re-run them
 * all. The cache key is the audience document, so the editor and the list
 * share an entry whenever they ask about the same audience.
 */
export function useAudiencePreview(
  audience: Audience | null,
  /**
   * Whether the campaign has a call step. Required, so no caller can forget it:
   * the server counts a referral with no date of birth as not ready for a
   * campaign that calls, whatever the audience's switch says (ADR-333).
   */
  placesCalls: boolean,
  opts?: { enabled?: boolean; staleTime?: number }
) {
  const key = JSON.stringify({ audience, placesCalls });
  return useQuery({
    queryKey: queryKeys.outreach.preview(key),
    queryFn: ({ signal }) => getOutreachClient().previewAudience(audience!, placesCalls, signal),
    enabled: (opts?.enabled ?? true) && !!audience,
    staleTime: opts?.staleTime ?? CACHE_TIME_1_MIN,
  });
}

/**
 * The attempt calendar a patient who never answers would receive.
 *
 * Runs only while a calendar is on screen (`enabled`): the editor's sticky
 * "If they never pick up" card on Steps and the Rehearse calendar (ADR-318,
 * G). Callers debounce the spec, so editing re-simulates once per pause, not
 * per keystroke; a hidden tab simulates nothing.
 */
export function useCampaignSimulation(input: {
  spec: CampaignSpec | null;
  outcome: string;
  locationId: string | null;
  enabled: boolean;
}) {
  const key = JSON.stringify(input.spec);
  return useQuery({
    queryKey: queryKeys.outreach.simulation(key, input.outcome, input.locationId),
    queryFn: ({ signal }) =>
      getOutreachClient().simulate(
        { spec: input.spec!, outcome: input.outcome, locationId: input.locationId },
        signal
      ),
    enabled: input.enabled && !!input.spec,
    staleTime: CACHE_TIME_1_MIN,
    // A spec that cannot simulate (an unopenable window, no location chosen)
    // fails the same way every time. Retrying only delays the message.
    retry: false,
  });
}

/**
 * The published outbound voice workflows a step may pick (ADR-318, F). Served
 * under `outreach:edit`, so an author needs no access to the workflow builder.
 */
export function useWorkflowOptions() {
  return useQuery({
    queryKey: queryKeys.outreach.workflowOptions(),
    queryFn: () => getOutreachClient().workflowOptions(),
    staleTime: CACHE_TIME_1_MIN,
  });
}

/**
 * Can this spec run? Every check, pass or not (ADR-318, R3). Keyed on the spec
 * document, like the preview: the editor asks about a spec that is saved
 * nowhere yet. Activate runs the same check on the server and refuses on a
 * failure, so this is guidance, not the gate.
 */
export function useLaunchCheck(spec: CampaignSpec | null, opts?: { enabled?: boolean }) {
  const key = JSON.stringify(spec);
  return useQuery({
    queryKey: queryKeys.outreach.launchCheck(key),
    queryFn: ({ signal }) => getOutreachClient().launchCheck(spec!, signal),
    enabled: (opts?.enabled ?? true) && !!spec,
    staleTime: CACHE_TIME_1_MIN,
    retry: false,
  });
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCampaignInput) => getOutreachClient().create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.outreach.campaigns() });
      toast.success('Campaign created');
    },
  });
}

export function useUpdateCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCampaignInput }) =>
      getOutreachClient().update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.outreach.campaigns() });
      queryClient.invalidateQueries({ queryKey: queryKeys.outreach.campaign(id) });
    },
  });
}

/**
 * Start or stop a campaign calling (ADR-318): its own endpoints under
 * `outreach:run`, no longer a field on the campaign update.
 */
export function useSetCampaignStatus(opts?: {
  /** The caller shows a refusal itself (the launch checks); no global error toast. */
  quiet?: boolean;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: opts?.quiet ? { suppressGlobalError: true } : undefined,
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'paused' }) =>
      status === 'active' ? getOutreachClient().activate(id) : getOutreachClient().pause(id),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.outreach.campaigns() });
      queryClient.invalidateQueries({ queryKey: queryKeys.outreach.campaign(id) });
    },
  });
}

/**
 * Publishing is also saving — there is no draft-spec storage, so an edited spec
 * exists only in the editor until this succeeds. The version list is invalidated
 * too, because publishing is the only thing that adds to it.
 */
export function usePublishCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PublishCampaignInput }) =>
      getOutreachClient().publish(id, data),
    onSuccess: (campaign, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.outreach.campaigns() });
      queryClient.invalidateQueries({ queryKey: queryKeys.outreach.campaign(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.outreach.versions(id) });
      toast.success(`Published version ${campaign.version ?? ''}`.trim());
    },
  });
}

export function useContactPolicy() {
  return useQuery({
    queryKey: queryKeys.outreach.contactPolicy(),
    queryFn: () => getOutreachClient().getContactPolicy(),
    staleTime: CACHE_TIME_10_MINS,
  });
}

export function useUpdateContactPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateContactPolicyInput) => getOutreachClient().updateContactPolicy(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.outreach.contactPolicy() });
      toast.success('Contact policy saved');
    },
  });
}

/**
 * Rebuild a campaign from its edited description.
 *
 * Invalidates nothing and shows no toast: it writes nothing on the server, and
 * its result is a spec the editor holds until someone publishes. The change
 * list it returns is the feedback, and it belongs beside the text that produced
 * it rather than in a corner of the screen.
 */
export function useCompileCampaign() {
  return useMutation({
    mutationFn: (data: CompileCampaignInput) => getOutreachClient().compile(data),
    // The panel renders the failure inline, beside the text that caused it.
    meta: { suppressGlobalError: true },
  });
}
